import React from "react";
import "../../styles/Programs.css";
const Programs = () => {
  return (
    <div className="programs">
      <h3>Introduction to Programs</h3>
      <p>
        {" "}
        All people reach a physiological peak at age 19. Muscle strength and
        endurance peak around this time or soon afterwards. After this we lose 1
        to 3% of our strength per year. People, such as those post-poliomyelitis
        or spinal cord injury or with neuromuscular diseases such as Duchenne
        muscular dystrophy, spinal muscular atrophy, amyotrophic lateral
        sclerosis or others conditions, peak with less strength and have
        difficulties at an earlier age than the rest of us. The difficulties
        encountered from muscular weakness are in day-to-day functioning and in
        breathing and coughing due to weakness of inspiratory and expiratory
        (respiratory) muscles. Even in "normal" elderly people in nursing care
        facilities complications related to respiratory muscle weakness are a
        leading cause of suffering and death (Article 65 of Bibliography).
      </p>
      <p>
        People with generalized weakness can have difficulty functioning that
        can be abetted with the use of assistive equipment and training (see
        Further Information). These conditions can also be a threat to survival
        when breathing, coughing, throat, and heart muscles are very weakened.
      </p>
      <p>
        Fortunately, today, much can be done to avoid suffering and death from
        these causes. Typically, muscular conditions as opposed to neurological
        conditions do not affect the heart muscle. However, all people with
        myopathies like Duchenne muscular dystrophy should be evaluated for
        heart muscle weakness regularly. This is particularly true today because
        we have new medications and medical approaches to improving heart
        function (see Article 135 of Bibliography).
      </p>
      <p>
        Typically people with weak breathing and coughing muscles are all right
        until a simple cold causes the production of airway secretions that the
        person is too weak to cough out. Bacteria multiple in the secretions and
        the person develops pneumonia and respiratory distress. He or she is
        taken to a local hospital where oxygen is given and when breathing is
        suppressed by the delivered oxygen, a tube is placed through the nose or
        mouth into the lungs to evacuate the secretions and provide breathing
        support. While receiving support through the invasive tube, breathing on
        one's own may be delayed or thought to be impossible. The patient is
        then told that a tracheostomy tube is needed. This is a tube that is
        passed through the neck and into the airway. Typically, the
        hospitalization during which a person undergoes tracheostomy following
        pneumonia is 72 days. In reality, virtually no one who can speak needs
        or should ever receive a tracheostomy tube and those who have them
        should consider having them removed, whether needing to use a ventilator
        or not. After tube removal the great majority of ventilator users can
        wean off of supported ventilation whereas this rarely happens when one
        has a tracheostomy tube (see Article 76 of the Bibliography).
      </p>
      <p>
        Unfortunately, people are left to develop pneumonia after pneumonia,
        have hospitalization after hospitalization, and develop breathing
        difficulties and they are not offered the respiratory muscle aids that
        can prevent these problems. All that is typically done is that patients
        are given oxygen that only makes the problem worse and told that they
        must undergo tracheostomy to survive. Yet, all people with experience
        using tracheostomy tubes and noninvasive inspiratory and expiratory
        muscle aids greatly prefer the latter (Article 15 of the Bibliography).
      </p>{" "}
      <h3>What Are Inspiratory and Expiratory Muscle Aids</h3>
      <p>
        Inspiratory and expiratory muscle aids are devices and techniques that
        involve the manual or mechanical application of forces to the body or
        intermittent pressure changes to the airway to assist inspiratory or
        expiratory muscle function. The most important inspiratory aid is to
        receive air under pressure when one inhales (intermittent positive
        pressure ventilation or IPPV). The most important expiratory aid is to
        have a negative pressure (vacuum) applied to the airway via the nose and
        mouth when one coughs along with a manual thrust to the belly to further
        increase cough flows.
      </p>
      <h3>The Intervention Objectives</h3>
      <p>
        No one should receive supplemental oxygen, bronchodilators, or other
        medications as an alternative to normalizing blood oxygen levels by
        normalizing lung ventilation. Using oxygen rather than assisted
        ventilation results in worsening of carbon dioxide retention and
        inevitably results in respiratory failure.
      </p>
      <h4>Goal One: Maintain Lung Range-of-Motion</h4>
      <p>
        Lungs become stiff if not expanded regularly. The vital capacity is the
        deepest breath that one can blow into a device that measures it
        (spirometer). If the vital capacity is 500 ml but the predicted capacity
        is 5000 ml then without assistance one can only expand about 10% of
        one's lungs and the rest closes down and for children does not grow
        properly. Use of incentive spirometry or deep breathing is useless
        because they do not expand the lungs more than about 10%. Mobilization
        of the lungs to prevent chest wall contractures and lung restriction can
        only be achieved by providing regular deep volumes of air
        (insufflations) or overnight deep breaths (IPPV).
      </p>
      <p>
        A person's maximum insufflation capacity (MIC) is determined by giving
        the person the largest volume of air that he or she can hold with the
        throat closed. This is usually done by teaching the person to stack
        volumes of air consecutively delivered from a manual resuscitator. The
        person holds the stacked air with the (throat) closed glottis until no
        more air can be held. Patients who learn glossopharyngeal (frog)
        breathing can often air stack to the MIC without mechanical assistance.
      </p>
      <p>
        The primary objectives in using air stacking or in providing maximum
        insufflations as lung and chest wall range-of-motion are to: increase
        the MIC, to maximize cough flows, to maintain or improve lung
        elasticity, to prevent or eliminate atelectasis, and to master
        noninvasive ventilation (noninvasive IPPV). The ability to air stack
        means that one can use noninvasive ventilation and assisted coughing to
        prevent pneumonia, respiratory failure, or ever need to undergo
        tracheostomy. Air stacking can also increase voice volume, facilitate
        eating, and promote lung growth in children.
      </p>
      <p>
        Since anyone who can air stack is also able to use noninvasive IPPV, if
        such a patient is intubated for respiratory failure, he or she can be
        extubated directly to continuous noninvasive IPPV whether or not able to
        breathe independently (see Further Information).
      </p>
      <h4>
        Goal Two: Continuously Maintain Normal Lung Ventilation by Assisting
        Inspiratory Muscles As Needed Overnight
      </h4>
      <h5>Noninvasive IPPV</h5>
      <p>
        IPPV can be noninvasively delivered via mouthpieces, nasal, and
        oral-nasal interfaces for nocturnal ventilatory assistance (see Further
        Information). Many people with no ability to breathe on their own and no
        upper limb function keep simple Respironics (Murrysville, PA) 15 mm or
        22 mm angled mouthpieces near their mouths and grab them between their
        teeth as needed to receive mouthpiece IPPV during daytime hours.
        Mouthpiece IPPV is the most important method of daytime ventilatory
        support. Some people keep the mouthpiece between their teeth all day.
        Most prefer to have the mouthpiece held near the mouth. A metal clamp
        attached to a wheelchair can be used for this purpose or the mouthpiece
        can be fixed onto motorized wheelchair controls, most often, sip and
        puff, chin, or tongue controls.
      </p>
      <p>
        The ventilator is set for much greater than normal tidal volumes, often
        from 1000 to 2000 ml. The person grabs the mouthpiece with his mouth and
        supplements or substitutes for inadequate breath volumes. The person
        varies the volume of air taken from ventilator cycle to ventilator cycle
        and breath to breath to vary speech volume and cough flows as well as to
        air stack to fully expand the lungs.
      </p>
      <p>
        To use mouthpiece IPPV effectively and conveniently, adequate neck
        rotation and oral motor function are necessary to grab the mouthpiece
        and receive IPPV without insufflation leakage. Since the low pressure
        alarms of volume-cycled ventilators can often not be turned off, to
        prevent their sounding during routine daytime IPPV when not every
        delivered volume is received by the patient, a flexed mouthpiece for
        IPPV or an in-line regenerative humidifier can be used. These create 2
        or 3 cm H2O back pressure which is adequate to prevent the low pressure
        alarm from sounding.
      </p>
      <p>
        The lipseal can provide an essentially closed system of noninvasive
        ventilatory support when using mouthpiece IPPV during sleep. Lipseal
        IPPV is delivered during sleep with little loss of air out of the mouth
        and with virtually no risk of the mouthpiece falling out of the mouth.
        Orthodontic bite plates and custom fabricated acrylic lipseals can also
        increase comfort and effectiveness. Typically high ventilator
        insufflation volumes of 1000 to 2000 ml compensate for air leakage out
        of the nose during sleep.
      </p>
      <p>
        Because people prefer to use mouthpiece IPPV or the intermittent
        abdominal pressure ventilator for daytime use (see Further Information),
        nasal IPPV (or the noninvasive delivery of IPPV via a nasal interface
        ("CPAP mask") is most practical only for nocturnal use. Daytime nasal
        IPPV is indicated for those who cannot grab or retain a mouthpiece
        because of oral muscle weakness, inadequate jaw opening, or insufficient
        neck movement. Twenty-four hour nasal IPPV can, nevertheless, be a
        viable and desirable alternative to tracheostomy even for some people
        with severe lip and mouth muscle weakness.
      </p>
      <p>
        Most people prefer to use IPPV via a nasal rather than oral interface
        during sleep. Whether using nocturnal nasal or lipseal IPPV in a regimen
        of 24 hour noninvasive IPPV, and despite the maintenance of normal
        daytime alveolar ventilation, about 3% of people with no ability to
        breathe on their own have episodes of excessive air loss during sleep.
        These often result in arousals with shortness of breath. The person may
        also complain of recurrence of morning headaches, fatigue, and perhaps
        nightmares and anxiety. The nasal ventilation user should usually be
        switched to lipseal IPPV and lipseal IPPV users can have their systems
        "closed" by having their nostrils clipped or plugged with cotton kept in
        by covering the nostrils by a Band-Aid during sleep. Another practical
        solution is to set the ventilator's low pressure alarm at a level that,
        by its sounding, stimulates the sleeper sufficiently to shorten periods
        of air leaking during sleep. Commonly, a low pressure alarm setting of
        10 to 20 cm H2O pressure is used for this purpose and the user develops
        sleep reflexes to prevent prolonged air leakage.
      </p>
      <p>
        There are now numerous commercially available nasal interfaces (CPAP
        masks). These include the Monark and gel masks from Respironics Inc.,
        Murrysville, CO, the ResCare Inc. (San Diego) Sullivan mask, the
        SleepNet (Manchester, N.H.) Phantom and IQ Nasal Masks, and Mallincrodt
        interfaces (Pleasanton, CA). Each interface design applies pressure
        differently to the paranasal area. One cannot predict which model will
        be most effective and preferred by any particular user. Skin pressure
        and insufflation leakage into the eyes are common complaints with
        several of these generic models. Such difficulties resulted in the
        fabrication of interfaces that mold themselves to facial tissues and of
        other custom molded interface designs (see Further Information). People
        must be offered trials using various nasal interfaces and are encouraged
        to choose between them. Interface use is evaluated for comfort and seal
        around the nose. No one should be offered and expected to use only one
        nasal interface anymore. Alternating IPPV interfaces nightly alternates
        skin pressure sites, minimizes discomfort, and is to be encouraged.
      </p>
      <p>
        People whose blood carbon dioxide levels increasing during the day
        causing their blood oxygen levels to decrease below 95% need to use
        noninvasive IPPV, usually mouthpiece IPPV, for periods of time during
        daytime hours. Failure to maintain normal lung ventilation during
        daytime hours will result in inadequate nocturnal benefit from the use
        of noninvasive IPPV. For patients not wishing to switch to lipseal IPPV
        for nocturnal aid despite excessive air leakage out of the mouth, a
        chinstrap or plugged lipseal can be used to decrease mouth leakage. In
        the presence of nasal congestion people either use decongestants to
        permit nasal IPPV or they switch to mouthpiece and lipseal IPPV, or on
        rare occasions, temporary use of a body ventilator like a portable iron
        lung. Most often the person continues nasal IPPV using decongestants.
      </p>
      <p>
        Because of the need for air stacking, people over 5 years old whose MICs
        exceed their vital capacities need to use volume cycled portable
        ventilators rather than pressure cycled machines like BiPAP because the
        latter cannot provide optimally deep breaths or permit users to stack
        breaths.
      </p>
      <p>
        In summary, noninvasive IPPV can be used for up to full-time ventilatory
        support for the great majority of people with no ability to breathe on
        their own provided that they have mouth muscle function sufficient for
        speaking.
      </p>{" "}
      <h4>
        {" "}
        Goal Three: Provide Functional Coughs by Assisting Expiratory Muscles
      </h4>
      <h5>Why Are Expiratory Muscle Aids Needed</h5>
      <p>
        Illness and death in people with generalized weakness is almost always
        due to respiratory difficulty that occurs because of a weak cough.
        Breathing (inspiratory), expiratory, and throat (bulbar) muscles are
        needed for effective coughing. The latter are predominantly the
        abdominal muscles. Clearing airway secretions can be a continual problem
        but it most often occurs during chest infections and following general
        anesthesia for surgery for any reason.
      </p>
      <h5>Manually Assisted Coughing</h5>
      <p>
        Peak cough flows (PCF) most exceed 160 liters per minute to be effective
        for coughing up airway debris. PCF are increased by manually assisted
        coughing. If the vital capacity is less than 1.5 liters, insufflating or
        air stacking to the MIC becomes crucial to optimize cough flows. Once
        the person is insufflated to the MIC, an abdominal thrust is timed to
        the cough to increase the flow. Techniques of manually assisted coughing
        involve different hand and arm placements for thrusts (see Further
        Information). A belly thrust with one hand while applying
        counterpressure to the chest with the other arm and hand further
        increases assisted PCF for 20% of people.
      </p>
      <p>
        Manually assisted coughing requires a cooperative patient, good
        coordination between the patient and care giver, and adequate physical
        effort and often frequent application by the care giver. It is usually
        ineffective in the presence of significant back deformity.
      </p>{" "}
      <p>
        Abdominal compressions should not be used for one and one-half hours
        following a meal, however, chest compressions can be used to augment
        PCF. When inadequate, the most effective alternative for generating
        optimal PCF and clearing airway secretions is the use of MI-E.
      </p>
      <p>
        The inability to generate over 160 liters per minute of assisted PCF
        despite having a vital capacity or MIC greater than 1 liter usually
        indicates fixed upper airway obstruction or severe throat muscle
        weakness and airway collapse during coughing attempts. Vocal cord
        adhesions or paralysis may have resulted from a previous translaryngeal
        intubation or tracheostomy. Some lesions, especially the presence of
        obstructing granulation tissue, can be corrected surgically.
      </p>{" "}
      <h5>Mechanical Insufflation-Exsufflation (MI-E)</h5>
      <p>
        Mechanical Cough Assist devices (J. H. Emerson Co., Cambridge, MA)
        deliver deep insufflations followed immediately by deep exsufflations.
        The insufflation and exsufflation pressures and delivery times are
        independently adjustable. Except after a meal, an abdominal thrust is
        applied in conjunction with the exsufflation. MI-E can be provided via
        an oral-nasal interface, a simple mouthpiece, or via an invasive airway
        tube like a tracheostomy tube. When delivered via the latter, the cuff,
        when present, should be inflated.
      </p>
      <p>
        {" "}
        The Cough-Assist can be manually or automatically cycled. Manual cycling
        facilitates care giver-user coordination of inspiration and expiration
        with insufflation and exsufflation, but it requires hands to deliver an
        abdominal thrust, to hold the mask on the patient, and to cycle the
        machine. One treatment consists of about five cycles of MI-E followed by
        a short period of normal breathing or ventilator use to avoid
        hyperventilation. Insufflation and exsufflation pressures are almost
        always from +35 to +60 cm H2O to -35 to -60 cm H2O. Most patients use 35
        to 45 cm H2O pressures insufflations and exsufflations. In experimental
        models, +40 to -40 cm H2O pressures have been shown to provide maximum
        forced deflation volumes and flows (see Further Information). Multiple
        treatments are given in one sitting until no further secretions are
        expulsed and any secretion or mucus induced oxygen desaturations are
        reversed. Use can be required as frequently as every few minutes around
        the clock during chest colds. Although no medications are usually
        required for effective MI-E in people with weak muscles, liquefaction of
        sputum using heated aerosol treatments may facilitate exsufflation when
        secretions are inspissated.
      </p>
    </div>
  );
};

export default Programs;
